import { useMemo, useEffect } from "react";
import {
  useTable,
  useColumnOrder,
  useGlobalFilter,
  useFilters,
  useGroupBy,
  useSortBy,
  useExpanded,
  useFlexLayout,
  usePagination,
  useResizeColumns,
  useRowSelect,
  //   TableOptions,
  //   Column,
  //   TableInstance,
  //   Row,
  //   Cell,
} from "react-table";

import { Button } from "react-bootstrap";

import { BsDash, BsPlus } from "react-icons/bs";

import { notEmpty } from "../../../helpers/utilities";

import TextFieldColumnFilter from "../TextFieldColumnFilter";

import HeaderCheckbox from "./HeaderCheckbox";
import RowCheckbox from "./RowCheckbox";

import { dateEqualFilterFn } from "../DateColumnFilter";
import { deepEqualFilterFn } from "../SelectColumnFilter";

const useSelectionUi = (hooks) => {
  hooks.visibleColumns.push((columns) => [
    {
      id: "_selector",
      disableResizing: true,
      disableGroupBy: true,
      minWidth: 45,
      width: 45,
      maxWidth: 45,

      Header: HeaderCheckbox,
      Cell: RowCheckbox,
    },
    ...columns,
  ]);
  hooks.useInstanceBeforeDimensions.push(({ headerGroups }) => {
    const selectionGroupHeader = headerGroups[0].headers[0];
    selectionGroupHeader.canResize = false;
  });
};

const getHooks = (enableRowSelection) => {
  let defaultHooks = [
    useColumnOrder,
    useGlobalFilter,
    useFilters,
    useGroupBy,
    useSortBy,
    useExpanded,
    useFlexLayout,
    usePagination,
    useResizeColumns,
  ];

  defaultHooks = enableRowSelection
    ? [...defaultHooks, useRowSelect, useSelectionUi]
    : defaultHooks;

  return defaultHooks.filter(notEmpty);
};

const defaultFilterFn = (rows, id, filterValue) =>
  rows.filter((row) => {
    const rowValue = row.values[id];

    if (!rowValue) return true;

    return String(rowValue)
      .toLowerCase()
      .includes(String(filterValue.trim()).toLowerCase());
  });

const tableDateEqualFn = dateEqualFilterFn;
const tableDeepEqualFn = deepEqualFilterFn;

tableDateEqualFn.autoRemove = (val) => !val;
tableDeepEqualFn.autoRemove = (val) => !val;

const filterTypes = {
  text: defaultFilterFn,
  dateEqual: tableDateEqualFn,
  deepEqual: tableDeepEqualFn,
};

const useDataTable = ({
  columns,
  data,
  initialState: userInitialState = {},
  enableRowSelection = false,
  enableDetailPanel = false,
  onPaginationChange,
  onColumnFiltersChange,
  onSortingChange,
  // required for server-side pagination, sorting, and filtering
  totalRows = 0,

  ...rest
}) => {
  if (!columns || !data) {
    throw new Error("columns and data are required for tableOptions");
  }

  const defaultColumn = useMemo(
    () => ({
      Filter: TextFieldColumnFilter,
      filter: "text",
      // When using the useFlexLayout:
      minWidth: 30, // minWidth is only used as a limit for resizing
      width: 150, // width is used for both the flex-basis and flex-grow
      maxWidth: 200, // maxWidth is only used as a limit for resizing
    }),
    [],
  );

  const columnsWithDetailPanelColumn = useMemo(
    () =>
      enableDetailPanel
        ? [
            {
              Header: () => null,
              id: "_expander",
              Cell: ({ row }) => (
                <span {...row.getToggleRowExpandedProps()}>
                  {/* <MdOutlineExpandLess
                    className={`tw-text-lg tw-transition-transform ${
                      row.isExpanded ? "tw-rotate-180" : "tw-rotate-0"
                    }`}
                  /> */}
                  {row?.isExpanded ? (
                    <Button
                      className="order-history-expander-button"
                      variant="primary"
                    >
                      <BsDash />
                    </Button>
                  ) : (
                    <Button
                      className="order-history-expander-button"
                      variant="primary"
                    >
                      <BsPlus />
                    </Button>
                  )}
                </span>
              ),
              width: 50,
              disableResizing: true,
              SubCell: () => null,
            },
            ...columns,
          ]
        : columns,
    [columns, enableDetailPanel],
  );

  const initialState = useMemo(() => userInitialState, [userInitialState]);

  const instance = useTable(
    {
      columns: columnsWithDetailPanelColumn,
      data,
      initialState,
      defaultColumn,
      filterTypes,
      // autoResetPage: false, // may have to remove this depending on use case
      autoResetSelectedRows: true,
      autoResetSortBy: false,
      autoResetFilters: false,
      disableSortRemove: true,
      totalRows,
      ...rest,
    },
    ...getHooks(enableRowSelection),
  );

  const {
    state: { pageIndex, pageSize, sortBy, filters },
  } = instance;

  useEffect(() => {
    onPaginationChange?.({ pageIndex, pageSize });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, pageSize]);

  useEffect(() => {
    onSortingChange?.(sortBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy]);

  useEffect(() => {
    onColumnFiltersChange?.(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return instance;
};

export default useDataTable;
